import { useMemo } from 'react';

import AppModal from "@/components/AppModal";

import AppLoadingButton from "@/components/mui/LoadingButton";
import {
  Box,
  IconButton,
  OutlinedInput,
  Stack,
  Typography,
  inputClasses,
  sliderClasses,
  styled,
} from "@mui/material";
import Backdrop from "@mui/material/Backdrop";
// import {OutlinedInput} from "@/components/mui";
import RpIcon, { RpIconColor } from "@/components/RpIcon.jsx";
import useSettings from "@/hook/useSettings";
import { enqueueSnackbar } from "notistack";
import { useEffect } from "react";
import { useTranslation } from "react-i18next";
import classes from "./index.module.css";

import Slider from "@/components/Slider";
import { useFormik } from "formik";
import * as Yup from "yup";
import styles from "./index.module.css";

import { getCardLimit, setCardLimit } from "@/services";
import { useBoolean, usePrevious, useRequest } from "ahooks";
import { useSelector } from "react-redux";
import { getRegExpByPrecision } from "@/utils/utils";
import AppTextField from "@/components/AppTextField";
import SimpleHeader from "@/pages/components/Headers/SimpleHeader.tsx";

const StyledInput = styled(OutlinedInput)(({ theme }) => {
  return {
    color: theme.palette.primary.main,
    "caret-color": theme.palette.brand.main,
    "&::before": {
      display: "none",
    },
    "&::after": {
      display: "none",
    },
    [`& .${inputClasses.input}`]: {},
    "& input[type=number]": {
      "-moz-appearance": "textfield",
    },
    "& input[type=number]::-webkit-outer-spin-button": {
      "-webkit-appearance": "none",
      margin: 0,
    },
    "& input[type=number]::-webkit-inner-spin-button": {
      "-webkit-appearance": "none",
      margin: 0,
    },
  };
});

interface Props {
  onClose?: () => void;
  reresh: () => void;
  open: boolean;
  card: any;
}

const StyledSlider = styled(Slider)(({ theme }) => {
  return {
    color: theme.palette.text.primary,
    [`& .${sliderClasses.rail}`]: {
      backgroundColor: theme.palette.text.secondary,
    },
    [`& .${sliderClasses.thumb}`]: {
      // border: 'none',
      // width: 16,
      // height: 16,
      backgroundColor: "#FFF",
    },
    "& .MuiSlider-markLabel": {
      fontSize: 12,
      fontWeight: "normal",
      backgroundColor: "unset",
      color: theme.palette.textColor,
      "&::before": {
        display: "none",
      },
    },
    "& .MuiSlider-markLabelActive": {
      color: theme.palette.textColor,
    },
  };
});

export default function CardLimitAmountModal(props: Props) {
  const { open, card, onClose } = props;
  const { t } = useTranslation();
  const { settings } = useSettings();

  const [signOpen, signOpenOperate] = useBoolean(false);

  const { user, isAuthenticated, homeInfo } = useSelector((state) => state.user);

  const previousCardId = usePrevious(card?.cardId);

  const getCardLimitReq = useRequest(
    async () => {
      const res = await getCardLimit(card.cardId);
      return res;
    },
    { manual: true }
  );

  const setCardLimitReq = useRequest(
    async (signature, message) => {
      const res = await setCardLimit(
        card.cardId,
        values.single,
        values.single,
        signature,
        message
      );
      return res;
    },
    { manual: true }
  );

  useEffect(() => {
    if (getCardLimitReq?.data) {
      const single = parseFloat(getCardLimitReq?.data.single)
      setFieldValue("single", single);
      // setFieldValue("daily", parseFloat(getCardLimitReq?.data.daily || 0) + 0);
    }
  }, [getCardLimitReq?.data]);

  const validationSchema = Yup.object().shape({
    single: Yup.string().trim().required("this_field_is_required"),
    // daily: Yup.string().trim().required("this_field_is_required"),
  });

  useEffect(() => {
    if (card?.cardId != previousCardId) {
      getCardLimitReq.runAsync();
    }
  }, [card?.cardId]);

  const _onSave = (values) => {
    signOpenOperate.setTrue();
    setCardLimitReq.runAsync().then(res => {
      enqueueSnackbar(t('successful'), { variant: 'success' })
      signOpenOperate.setFalse();
      onClose && onClose();

    })
  };
  const {
    errors,
    values,
    touched,
    setValues,
    setFieldValue,
    handleChange,
    handleBlur,
    handleSubmit,
  } = useFormik({
    initialValues: {
      single: "",
      daily: "",
    },
    validationSchema,
    validateOnBlur: true,
    validateOnChange: true,
    validateOnMount: true,
    onSubmit: _onSave,
  });
  const marks = useMemo(() => {
    if (getCardLimitReq?.data?.singleMax && getCardLimitReq?.data?.singleMax > 0) {
      const _marks = []
      _marks.push({ label: "0", value: 0 })
      for (let i = 1; i <= 5; i++) {
        _marks.push(
          {
            label: getCardLimitReq?.data?.singleMax * i / 5, value: getCardLimitReq?.data?.singleMax * i / 5
          }
        )
      }
      return _marks
    }
    return [
      { label: "0", value: 0 },
      { label: "25,000", value: 25000 },
      { label: "50,000", value: 50000 },
      { label: "75,000", value: 75000 },
      { label: "100,000", value: 100000 },
    ]
  }, [getCardLimitReq?.data]);


  const onAmountChange = (e) => {
    const val = e.target.value;
    let filteredInput = val.replace(/[^\d.]/g, "")
      .replace(/^\./g, "")
      .replace(/^0\d[0-9]*/g, "0");

    const reg = getRegExpByPrecision(0);

    if (filteredInput === "" || reg.test(filteredInput)) {
      setFieldValue('single', filteredInput)
    }
  }

  const canSave = useMemo(() => {
    return values.single !== "" && parseFloat(values.single) > 0;
  }, [values.single])

  const singleMax = useMemo(() => {
    if (!getCardLimitReq?.data) {
      return 0
    }
    if (getCardLimitReq?.data?.singleMax) {
      return parseFloat(getCardLimitReq?.data?.singleMax)
    }
    return 0
  }, [getCardLimitReq?.data]);
  0


  return (
    <>
      <AppModal
        title={t("transaction_limit")}
        open={open}
        onClose={() => {
          onClose();
        }}
        closeAfterTransition
        slots={{ backdrop: Backdrop }}
        slotProps={{
          backdrop: {
            timeout: 500,
          },
        }}
      >
        <Box className={classes.container}>
          <SimpleHeader
            title={t('transaction_limit')}
            closeButton={<RpIcon icon="close"
              clickable
              onClick={() => {
                props?.onClose();
              }}
              size={22} />}
            sx={{
              'borderTopLeftRadius': '12px',
              'borderTopRightRadius': ' 12px',
            }}
          />
          {/*<Stack*/}
          {/*  direction="row"*/}
          {/*  justifyContent="space-between"*/}
          {/*  alignItems="center"*/}
          {/*  color="textColor"*/}
          {/*>*/}
          {/*  <RpIcon icon="multiplication_sign"*/}
          {/*          clickable*/}
          {/*          onClick={() => {*/}
          {/*            props?.onClose();*/}
          {/*          }}*/}
          {/*          size={22} />*/}
          {/*  <Typography sx={{flex: 1, textAlign: "center"}}>*/}
          {/*    {t("transaction_limit")}*/}
          {/*  </Typography>*/}
          {/*</Stack>*/}
          <Box className={styles.formWrap}>
            <form onSubmit={handleSubmit}>
              <Box className={styles["form-item"]}>
                <Typography>{t("single_limit")}</Typography>
                <AppTextField
                  disabled={getCardLimitReq?.loading}
                  fullWidth
                  type="number"
                  // label={t("single_limit")}
                  name="single"
                  value={values.single}
                  onChange={onAmountChange}
                />
                {/*<StyledInput*/}
                {/*  disabled={getCardLimitReq?.loading}*/}
                {/*  fullWidth*/}
                {/*  type="number"*/}
                {/*  // label={t("single_limit")}*/}
                {/*  name="single"*/}
                {/*  value={values.single}*/}
                {/*  onChange={onAmountChange }*/}
                {/*/>*/}
                <Box className={styles.slider}>
                  <StyledSlider
                    disabled={getCardLimitReq?.loading}
                    aria-label="Always visible"
                    marks={marks}
                    step={10000}
                    defaultValue={0}
                    valueLabelDisplay="off"
                    getAriaValueText={(value) => `${value}`}
                    // user?.kycCardLimit ? parseFloat(user?.kycCardLimit) :
                    max={singleMax}
                    min={0}
                    value={values.single}
                    onChange={(e) => setFieldValue("single", e.target.value)}
                  />
                </Box>
              </Box>
              <AppLoadingButton
                disabled={!canSave}
                type="submit"
                loading={setCardLimitReq.loading}
                variant={"contained"}
                fullWidth
              >
                {t("confirm")}
              </AppLoadingButton>
            </form>
          </Box>
        </Box>
      </AppModal>
    </>
  );
}
